'use client';

import { Box, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';

import { StyledListItemButton } from '.';

import type { BoxProps, ListItemButtonProps } from '@mui/material';
import type { FC } from 'react';

interface ListItemStyleProps extends ListItemButtonProps {
  activeRoot?: boolean;
  activeSub?: boolean;
}

const ListItemStyle = styled( ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'activeRoot' && prop !== 'activeSub',
} )<ListItemStyleProps>(({ activeRoot, activeSub, theme }) => ({
  ...theme.typography.body2,
  height: 100,
  position: 'relative',
  // textTransform: 'capitalize',
  paddingLeft: theme.spacing( 5 ),
  paddingRight: theme.spacing( 2.5 ),
  color: theme.palette.text.secondary,
  ...(activeRoot && {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity
    ),
    '&:before': {
      top: 0,
      right: 0,
      width: 3,
      bottom: 0,
      content: '\'\'',
      display: 'block',
      position: 'absolute',
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
      backgroundColor: theme.palette.primary.main,
    },
  }),

  ...(activeSub && {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
  }),
}));

const ListItemIconStyle = styled( ListItemIcon )( {
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    width: 22,
    height: 22,
  },
} );

interface ListSubItemIconStyleProps extends BoxProps {
  active?: boolean;
}

const ListSubItemIconStyle = styled( Box, {
  shouldForwardProp: (prop) => prop !== 'active',
} )<ListSubItemIconStyleProps>(({ active, theme }) => ({
  width: 4,
  height: 4,
  display: 'flex',
  borderRadius: '50%',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.text.disabled,
  transition: theme.transitions.create( 'transform' ),
  ...(active && {
    transform: 'scale(2)',
    backgroundColor: theme.palette.primary.main,
  }),
}));

interface NavListItemProps {
  title: string;
  subheader: string;
  path: string;
  icon?: JSX.Element;
  info?: JSX.Element;
  children?: Array<{
    title: string;
    path: string;
  }>;
}

export const NavListItem: FC<NavListItemProps> = ({
  title = '',
  subheader = '',
  path = '',
}) => {
  const pathname = usePathname();

  const isActive = pathname === path; // || asPath === path;

  return (
    // <ListItem dense key={title} disablePadding sx={{ px: 0 }}>
    <NextLink key={title} href={path} passHref legacyBehavior>
      <ListItemStyle activeSub={isActive}>
        <ListItemIconStyle>
          <ListSubItemIconStyle component="span" active={isActive} />
        </ListItemIconStyle>
        <StyledListItemButton active={isActive}>
          <ListItemText
            primaryTypographyProps={{
              variant: 'caption',
            }}
            secondaryTypographyProps={{
              variant: 'subtitle2',
            }}
            primary={subheader}
            secondary={title}
          />
        </StyledListItemButton>
      </ListItemStyle>
    </NextLink>
    // </ListItem>
  );
};
